import type { V2RateInfo } from '../types/v2';

/** Token symbols that are already USD-equivalent — their amount IS the USDT value.
 *  Match-by-uppercase; the set covers raw symbols and the wrapped ERC-20 forms. */
export const USD_PEGGED: ReadonlySet<string> = new Set([
  'USDT', 'USDC', 'USD', 'USDT_ERC20', 'USDC_ERC20',
]);

/** Quick membership check, case-insensitive. */
export const isUsdPegged = (symbol: string | null | undefined): boolean =>
  !!symbol && USD_PEGGED.has(symbol.toUpperCase());

/**
 * Convert `amount` of token `symbol` to its USDT equivalent using a rates
 * dictionary keyed by uppercase token symbol (shape returned by /api/v2/rates).
 *
 * Rules:
 *  - USD-pegged token → amount is already in USDT, returned verbatim.
 *  - Otherwise → amount × price_usd from the matching rate row.
 *  - Missing / non-numeric / zero / negative rate, or unparseable amount →
 *    returns null. Callers decide how to render "unknown" (typically by
 *    hiding the row, or falling back to the raw amount).
 *
 * Pure helper — no React, no fetches. Pair with `v2GetRates()` upstream.
 */
export const toUsdtAmount = (
  amount: number | string,
  symbol: string,
  rates: Record<string, V2RateInfo>,
): number | null => {
  const num = typeof amount === 'number' ? amount : parseFloat(amount);
  if (!Number.isFinite(num)) {
    return null;
  }
  if (isUsdPegged(symbol)) {
    return num;
  }
  const info = rates[symbol.toUpperCase()] ?? rates[symbol];
  const price = parseFloat(info?.price_usd ?? '');
  if (!Number.isFinite(price) || price <= 0) {
    return null;
  }
  return num * price;
};

/**
 * Format a USDT-equivalent value as the user-facing string used across
 * threshold rows ("100 000,00 USDT"). Space-separated thousands + comma
 * decimal — same European convention as `amountFormat.ts`.
 *
 * Pass `decimals` to override the default 2. Returns "—" for null /
 * non-finite input so cards can render the row even when the rate is
 * missing without sprouting an "NaN USDT".
 */
export const formatUsdtAmount = (value: number | null, decimals = 2): string => {
  if (value == null || !Number.isFinite(value)) {
    return '—';
  }
  const fixed = value.toFixed(decimals);
  const [intPart, decPart] = fixed.split('.');
  const intWithSep = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return decPart ? `${intWithSep},${decPart} USDT` : `${intWithSep} USDT`;
};
